import React, { useEffect, useRef, useState } from "react";
import {
    PreviewAvatar,
    PreviewFallback,
    VideoNamePill,
    VideoOverlay,
    VideoSlot,
    VideoStatusGroup,
    VideoStatusPill,
    VideoTile
} from "src/scenes/Chat/components/ChatCallPanel.styled";
import { attachVideoStream, getInitials } from "./ChatCallPanel.constants";
import MediaIcon from "./MediaIcon";

const DEFAULT_VIDEO_ASPECT_RATIO = 16 / 9;

const StreamVideo = ({
    activeSpeaker = false,
    audioMuted = false,
    avatarUrl = "",
    fallbackInitials = "",
    forceCameraOff = false,
    label,
    mirrored,
    muted = false,
    pip = false,
    screenPrimary = false,
    screenSharing = false,
    sinkId = "",
    stream,
    videoTrack = null,
    videoEffect = "none"
}) => {
    const videoRef = useRef(null);
    const [videoIsRendering, setVideoIsRendering] = useState(false);
    const [videoAspectRatio, setVideoAspectRatio] = useState(DEFAULT_VIDEO_ASPECT_RATIO);
    const videoTrackId = stream?.getVideoTracks?.()[0]?.id || "";
    const hasVideo = !forceCameraOff && stream?.getVideoTracks?.().length > 0;
    const speakerIsActive = activeSpeaker;

    useEffect(() => {
        setVideoIsRendering(false);
        const trackAspectRatio = Number(stream?.getVideoTracks?.()[0]?.getSettings?.().aspectRatio);
        setVideoAspectRatio(trackAspectRatio > 0 ? trackAspectRatio : DEFAULT_VIDEO_ASPECT_RATIO);
        const videoElement = videoRef.current;
        attachVideoStream(videoElement, hasVideo ? stream : null);

        if (hasVideo && videoTrack?.attach) {
            videoTrack.attach(videoElement);
        }

        return () => {
            if (videoTrack?.detach && videoElement) {
                videoTrack.detach(videoElement);
            }
        };
    }, [hasVideo, stream, videoTrack, videoTrackId]);

    useEffect(() => {
        const videoElement = videoRef.current;
        if (!videoElement?.setSinkId || muted) {
            return;
        }

        videoElement.setSinkId(sinkId || "").catch(error => {
            console.warn("Unable to change call speaker.", error);
        });
    }, [muted, sinkId]);

    useEffect(() => {
        const videoElement = videoRef.current;
        if (!videoElement || !hasVideo) {
            return undefined;
        }

        const markRendering = () => {
            if (videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
                setVideoAspectRatio(videoElement.videoWidth / videoElement.videoHeight);
            }
            setVideoIsRendering(true);
        };
        const markNotRendering = () => setVideoIsRendering(false);
        videoElement.addEventListener("loadeddata", markRendering);
        videoElement.addEventListener("playing", markRendering);
        videoElement.addEventListener("resize", markRendering);
        videoElement.addEventListener("stalled", markNotRendering);
        videoElement.addEventListener("emptied", markNotRendering);
        return () => {
            videoElement.removeEventListener("loadeddata", markRendering);
            videoElement.removeEventListener("playing", markRendering);
            videoElement.removeEventListener("resize", markRendering);
            videoElement.removeEventListener("stalled", markNotRendering);
            videoElement.removeEventListener("emptied", markNotRendering);
        };
    }, [hasVideo, stream, videoTrackId]);

    return (
        <VideoSlot
            $activeSpeaker={speakerIsActive}
            $aspectRatio={videoAspectRatio}
            $cameraOff={!hasVideo || !videoIsRendering}
            $pip={pip}
            $screenPrimary={screenPrimary}
        >
            {(!hasVideo || !videoIsRendering) && (
                <PreviewFallback>
                    <PreviewAvatar $src={avatarUrl}>{avatarUrl ? "" : fallbackInitials || getInitials(label)}</PreviewAvatar>
                </PreviewFallback>
            )}
            {hasVideo && (
                <VideoTile
                    ref={videoRef}
                    autoPlay
                    muted={muted}
                    playsInline
                    $effect={videoEffect}
                    $hidden={!videoIsRendering}
                    $mirrored={mirrored}
                />
            )}
            <VideoOverlay>
                <VideoNamePill>{label}</VideoNamePill>
                <VideoStatusGroup>
                    {speakerIsActive && <VideoStatusPill>Speaking</VideoStatusPill>}
                    {screenSharing && <VideoStatusPill>Sharing</VideoStatusPill>}
                    {forceCameraOff &&
                        <VideoStatusPill $danger $mutedIcon $iconOnly title="Camera off">
                            <MediaIcon type="video" disabled />
                        </VideoStatusPill>
                    }
                    {audioMuted &&
                        <VideoStatusPill $mutedIcon $iconOnly title="Muted">
                            <MediaIcon type="mic" disabled />
                        </VideoStatusPill>
                    }
                </VideoStatusGroup>
            </VideoOverlay>
        </VideoSlot>
    );
};

export default React.memo(StreamVideo);
